import React, { memo, useMemo, useRef, useState } from "react";
import AppBreadcrumb from "../common/AppBreadcrumb";
import { useQuery } from "react-query";
import axios from "../../config/axiosInstance";
import { USERS } from "../../config/apiConfig";
import QueriesTableSkeleton from "./QueriesTableSkeleton";
import ForbiddenQueriesPermission from "../common/ForbiddenQueriesPermission";
import { FaCrown, FaSearch, FaTimes } from "react-icons/fa";
import { motion } from "framer-motion";
import CustomCadastralQuery from "../queries/CustomCadastralQuery";
import { formatToReal } from "../../utils/utils";
import { useApplication } from "../../contexts/ApplicationContext";

const normalize = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const getQueryCategoryLabel = (query) => {
  const label = String(query?.queryCategory || "").trim();
  return label || "OUTROS";
};

const getCategoryPriority = (categoryLabel) => {
  const n = normalize(categoryLabel);
  if (n.includes("essencial")) return 0;
  if (n.includes("completo")) return 1;
  return 2;
};

const sortCategoriesWithPriority = (a, b) => {
  const pa = getCategoryPriority(a);
  const pb = getCategoryPriority(b);
  if (pa !== pb) return pa - pb;
  return String(a || "").localeCompare(String(b || ""), "pt-BR", {
    sensitivity: "base",
  });
};

const getCategoryStyle = (category) => {
  const n = normalize(category);
  if (n.includes("premium")) {
    return {
      accent: "bg-amber-400",
      badge: "bg-amber-50 text-amber-700 border border-amber-200",
    };
  }

  if (n.includes("essencial")) {
    return {
      accent: "bg-blue-400",
      badge: "bg-blue-50 text-blue-600 border border-blue-100",
    };
  }

  if (n.includes("completo")) {
    return {
      accent: "bg-green-400",
      badge: "bg-green-50 text-green-600 border border-green-100",
    };
  }

  return {
    accent: "bg-gray-400",
    badge: "bg-gray-50 text-gray-500 border border-slate-200",
  };
};

const cardVariants = {
  hidden: { opacity: 0, y: 4 },
  show: { opacity: 1, y: 0 },
};

const isPremiumCategory = (categoryLabel) =>
  normalize(categoryLabel).includes("premium");

const truncateText = (value, limit = 80) => {
  if (!value) return "";
  return value.length > limit ? `${value.slice(0, limit - 3)}...` : value;
};

const detectType = (q) => {
  const tRaw =
    q?.QueryCustomized?.parameterType ||
    q?.parameterType ||
    q?.parameter_type ||
    q?.queryParameterType;

  const t = String(tRaw || "").toUpperCase();
  if (t === "CPF" || t === "CNPJ" || t === "CPF/CNPJ") return t;

  const name = (q?.name || "").toLowerCase();
  if (name.includes("cpf") && name.includes("cnpj")) return "CPF/CNPJ";
  if (name.includes("cpf")) return "CPF";
  if (name.includes("cnpj") || name.includes("empresa")) return "CNPJ";
  return "";
};

const QueryCard = memo(function QueryCard({ query, index, handleQueryClick }) {
  const { user } = useApplication();
  const type = detectType(query);
  const statusActive = query?.status == 1;
  const showPrice =
    user?.Company?.showPriceQuery && Number.isFinite(Number(query?.price));
  const priceLabel = showPrice ? `${formatToReal(query.price)}/consulta` : "";

  return (
    <motion.div
      key={query.id}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.15 }}
      variants={cardVariants}
      transition={{
        duration: 0.22,
        ease: "easeOut",
        delay: Math.min(index * 0.03, 0.18),
      }}
      onClick={statusActive ? () => handleQueryClick(query) : undefined}
      className={`relative group cursor-pointer rounded-2xl border bg-white p-4 shadow-sm transition-all duration-300 ${
        statusActive
          ? "border-slate-200 hover:-translate-y-1 hover:border-blue-200 hover:shadow-md"
          : "border-gray-300 opacity-70 pointer-events-none shadow-sm"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="min-w-0 flex-1">
            <h3
              className="block w-full min-w-0 truncate text-[13px] font-semibold text-slate-900 uppercase"
              title={query.name}
            >
              {query.name}
            </h3>
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">
              {type || "CADASTRAL"}
            </p>
          </div>
        </div>
      </div>

      <p className="mt-2 text-[11px] text-slate-500">
        {truncateText(
          query?.description ||
            "Consulta completa com informações oficiais e referências atualizadas.",
          80,
        )}
      </p>

      <div className="mt-3 flex items-center justify-between text-[12px] text-slate-500">
        <span className="font-semibold text-slate-900">{priceLabel}</span>
        {statusActive && (
          <span className="text-[12px] font-semibold text-white rounded-full bg-gradient-to-r from-blue-600 to-blue-800 px-3 py-1 shadow-sm">
            CONSULTAR
          </span>
        )}
      </div>
    </motion.div>
  );
});

const QueriesCadastralTable = () => {
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isListVisible, setIsListVisible] = useState(true);
  const componentRef = useRef(null);

  const { isLoading, data: userQueryPermissions } = useQuery(
    ["userQueryPermissions", "cadastral"],
    async () => {
      const { data: items } = await axios.get(USERS.GET_USER_QUERY_PERMISSIONS);
      return items.data.filter(
        (query) =>
          query.categoryId === 2 && query.status != -1 && !query.isModular,
      );
    },
    {
      refetchOnMount: "always",
      staleTime: 0,
    },
  );

  const filteredQueries = useMemo(
    () =>
      (userQueryPermissions || []).filter((query) =>
        query?.name?.toLowerCase().includes(searchTerm?.toLowerCase()),
      ),
    [userQueryPermissions, searchTerm],
  );

  const sortedQueries = useMemo(
    () =>
      [...filteredQueries].sort((a, b) =>
        (a?.name || "").localeCompare(b?.name || ""),
      ),
    [filteredQueries],
  );

  const groupedQueries = useMemo(() => {
    const groups = [];
    const idxByCategory = new Map();

    (sortedQueries || []).forEach((query) => {
      const categoryLabel = getQueryCategoryLabel(query);
      if (!idxByCategory.has(categoryLabel)) {
        idxByCategory.set(categoryLabel, groups.length);
        groups.push({ category: categoryLabel, items: [] });
      }
      const idx = idxByCategory.get(categoryLabel);
      groups[idx].items.push(query);
    });

    return groups.sort((a, b) =>
      sortCategoriesWithPriority(a.category, b.category),
    );
  }, [sortedQueries]);

  const handleQueryClick = (query) => {
    setSelectedQuery(query);
    setSelectedPrice(query.price);
    setIsListVisible(false);
    setTimeout(() => {
      componentRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 200);
  };

  const handleBackClick = () => {
    setIsListVisible(true);
    setSelectedQuery(null);
  };

  const componentsMap = null;

  if (isLoading) return <QueriesTableSkeleton />;
  if (!userQueryPermissions?.length) return <ForbiddenQueriesPermission />;

  return (
    <div className="w-full">
      {isListVisible ? (
        <>
          <div className="mb-4 space-y-3">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar consultas cadastrais..."
                  className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 shadow-sm focus:ring-2 focus:ring-blue-600/30 focus:outline-none text-sm sm:text-base placeholder-gray-400 transition-all"
                />
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-700 pointer-events-none">
                  <FaSearch size={18} />
                </div>
                {!!searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-sm"
                    title="Limpar busca"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div
            style={{ minHeight: "300px" }}
            className="overflow-hidden relative mb-6"
          >
            {groupedQueries?.map((section) => {
              const catStyle = getCategoryStyle(section.category);
              return (
                <div key={section.category} className="mb-8 last:mb-0 mt-5">
                  <div className="mb-5 flex items-center gap-2.5">
                    <span
                      className={`inline-block h-6 w-[3px] shrink-0 rounded-full ${catStyle.accent} opacity-80`}
                    />
                    <h3
                      className="truncate text-[14px] font-semibold text-slate-800"
                      title={section.category}
                    >
                      <span className="inline-flex items-center gap-2">
                        {section.category}
                        {isPremiumCategory(section.category) && (
                          <FaCrown className="text-amber-500" size={14} />
                        )}
                      </span>
                    </h3>
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${catStyle.badge}`}
                    >
                      {section.items.length}{" "}
                      {section.items.length === 1 ? "consulta" : "consultas"}
                    </span>
                    <div className="h-px flex-1 bg-gradient-to-r from-slate-200 to-transparent" />
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 items-start">
                    {section.items.map((query, index) => (
                      <QueryCard
                        query={query}
                        index={index}
                        key={query.id}
                        handleQueryClick={handleQueryClick}
                      />
                    ))}
                  </div>
                </div>
              );
            })}

            {!sortedQueries?.length && !isLoading && (
              <div className="col-span-full text-center text-sm text-gray-500 py-10">
                Nenhuma consulta corresponde aos filtros aplicados.
              </div>
            )}
          </div>
        </>
      ) : (
        <div ref={componentRef}>
          <AppBreadcrumb
            items={[
              { label: "Consultas Cadastrais", onClick: handleBackClick },
            ]}
            currentLabel={selectedQuery?.name || "Detalhes"}
            className="mb-4"
          />

          {selectedQuery?.queryCustomizedId ? (
            <CustomCadastralQuery
              query={selectedQuery}
              selectedPrice={selectedPrice}
            />
          ) : (
            componentsMap[selectedQuery.id] || (
              <div className="text-gray-500 text-center mt-4">
                Componente não encontrado para esta consulta.
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default QueriesCadastralTable;
